import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1OrgProductsIdVariantsVariantIdBody,
  PostAdminV1OrgProductsIdVariantsBody,
  Variant,
} from '@nucleus-commerce/api-client/admin';

import { productsApi } from '../api/products-api';
import type { VariantsUpdateItem } from '../api/variant-mappers';

import { productKeys } from './product-keys';

/** 409 ALREADY_EXISTS (SKU or combination) and 422s are rendered by the dialog. */
export function useCreateVariant(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1OrgProductsIdVariantsBody) =>
      productsApi.createVariant(productId, body),
    meta: { successMessage: 'products:variants.toasts.created', silentError: true },
    onSuccess: (variant) => {
      queryClient.setQueryData<Variant[]>(productKeys.variants(productId), (current) =>
        current ? [...current, variant] : [variant],
      );
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: productKeys.detail(productId), exact: true }),
        queryClient.invalidateQueries({ queryKey: productKeys.lists() }),
      ]);
    },
  });
}

/** Grid and sheet edits: no success toast, the row simply shows the saved value; errors are rendered where the edit sits. */
export function useUpdateVariant(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      variantId,
      body,
    }: {
      variantId: string;
      body: PatchAdminV1OrgProductsIdVariantsVariantIdBody;
    }) => productsApi.updateVariant(productId, variantId, body),
    meta: { silentError: true },
    onSuccess: (variant) => {
      queryClient.setQueryData<Variant[]>(productKeys.variants(productId), (current) =>
        current?.map((v) => (v.id === variant.id ? variant : v)),
      );
    },
  });
}

export function useDeleteVariant(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) => productsApi.removeVariant(productId, variantId),
    meta: { successMessage: 'products:variants.toasts.deleted' },
    onSuccess: (_void, variantId) => {
      queryClient.setQueryData<Variant[]>(productKeys.variants(productId), (current) =>
        current?.filter((v) => v.id !== variantId),
      );
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: productKeys.detail(productId), exact: true }),
        queryClient.invalidateQueries({ queryKey: productKeys.lists() }),
      ]);
    },
  });
}

/**
 * The variant card's save: every changed row in one request. The card puts field errors on their
 * cells; the product's price range follows the new prices. One Save can run this next to the
 * product's own save, whose options create and archive variants: this answer may predate that, so
 * it updates its rows only and the list is read again once it's in.
 */
export function useUpdateVariants(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: VariantsUpdateItem[]) => productsApi.updateVariants(productId, items),
    meta: { silentError: true },
    onSuccess: (items) => {
      const saved = new Map(items.map((item) => [item.id, item]));
      queryClient.setQueryData<Variant[]>(productKeys.variants(productId), (current) =>
        current ? current.map((row) => saved.get(row.id) ?? row) : items,
      );
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) }),
        queryClient.invalidateQueries({ queryKey: productKeys.detail(productId), exact: true }),
        queryClient.invalidateQueries({ queryKey: productKeys.lists() }),
      ]);
    },
  });
}
